import { useCallback, useMemo } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { DateTime } from 'luxon';
import {
  TimeSignalsCountQuery,
  useTimeSignalsCountQuery,
  TimeSignalClassification,
  TimeSignalsCountQueryVariables
} from 'graphql/types';
import { MIN_SUPPORTED_DATE } from 'utils/date-time';
import {
  NotesClassification,
  useNotesFilterContext
} from '../NotesFilterContextProvider';
import { getLabelValues } from './useTimeSignalParam';

export const useTimeSignalsCountQueryVariables = (
  minDate: DateTime
): TimeSignalsCountQueryVariables => {
  const {
    filterContext: { selectedDateRange, searchCriteria }
  } = useNotesFilterContext();

  const otherFilters = useMemo(
    () => ({
      fromDate:
        selectedDateRange.start && selectedDateRange.start < minDate
          ? minDate.toISODate()
          : selectedDateRange.start?.toISODate() || undefined,
      toDate: selectedDateRange.end?.toISODate() ?? undefined,
      searchText:
        searchCriteria.keywords && searchCriteria.keywords.length > 0
          ? searchCriteria.keywords.join(' ')
          : undefined,
      labelValues: getLabelValues(searchCriteria)
    }),
    [selectedDateRange.start, selectedDateRange.end, minDate, searchCriteria]
  );

  return useMemo(
    () => ({
      inputUnClassified: {
        filter: {
          ...otherFilters,
          classifications: [TimeSignalClassification.Unclassified]
        }
      },
      inputAccepted: {
        filter: {
          ...otherFilters,
          classifications: [TimeSignalClassification.Accepted]
        }
      },
      inputPrivate: {
        filter: {
          ...otherFilters,
          classifications: [TimeSignalClassification.Private]
        }
      },
      inputTrash: {
        filter: {
          ...otherFilters,
          classifications: [TimeSignalClassification.Trash]
        }
      }
    }),
    [otherFilters]
  );
};

export const useTimeSignalsCount = (
  minDate: DateTime = MIN_SUPPORTED_DATE
): {
  data: TimeSignalsCountQuery | undefined;
  loading: boolean;
  countByType: (notesClassification: NotesClassification) => number | undefined;
  refetch: (
    variables?: TimeSignalsCountQueryVariables
  ) => Promise<ApolloQueryResult<TimeSignalsCountQuery>>;
} => {
  const param = useTimeSignalsCountQueryVariables(minDate);

  const { data, refetch } = useTimeSignalsCountQuery({
    variables: param
  });

  const isLoading = useMemo(() => !data, [data]);

  const countByType = useCallback(
    (notesClassification: NotesClassification): number | undefined => {
      if (!isLoading) {
        switch (notesClassification) {
          case 'All': {
            const unclassifedCount = data?.unClassified.totalCount ?? 0;
            const acceptedCount = data?.accepted.totalCount ?? 0;
            const privateCount = data?.private.totalCount ?? 0;

            return unclassifedCount + acceptedCount + privateCount;
          }
          case 'Unclassified':
            return data?.unClassified?.totalCount;
          case 'Accepted':
            return data?.accepted?.totalCount;
          case 'Private':
            return data?.private?.totalCount;
          case 'Trash':
            return data?.trash?.totalCount;
        }
      }
    },
    [data, isLoading]
  );

  return useMemo(() => ({ data, loading: isLoading, countByType, refetch }), [
    data,
    isLoading,
    countByType,
    refetch
  ]);
};
